/**
 * Notification Sound Utility
 * Plays a beep sound to alert staff of important events.
 *
 * iOS / iPadOS fix: Apple requires AudioContext.resume() to be called inside
 * a real user gesture (touchstart/mousedown) at least once before any
 * programmatic audio can play. We attach a one-time listener at module load
 * so the context is unlocked the moment the user first touches the screen —
 * well before any polling beep is triggered.
 *
 * Android / desktop fix: ctx.resume() is async. We chain all oscillator work
 * inside the .then() callback so the tone only starts after the context has
 * actually resumed.
 */

let audioContext = null;

/** Return the shared AudioContext, creating a new one if needed or if closed. */
const getAudioContext = () => {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

/** Fire the oscillator immediately — caller must ensure context is running. */
const _fireBeep = (ctx, frequency, duration, volume, type) => {
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.frequency.value = frequency;
  oscillator.type = type;
  gainNode.gain.setValueAtTime(volume, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration / 1000);
};

/**
 * iOS / iPadOS unlock: on the very first touch or mousedown anywhere on the
 * page, resume the AudioContext. After this one call the context stays
 * unlocked for all future programmatic (polling-triggered) beeps.
 */
const _unlockAudio = () => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  } catch (_) {}
  document.removeEventListener('touchstart', _unlockAudio, true);
  document.removeEventListener('mousedown',  _unlockAudio, true);
};

// Attach listeners immediately when this module is imported.
// { capture: true } ensures we fire before any other handler.
// { once } would also work but we remove manually for wider browser support.
document.addEventListener('touchstart', _unlockAudio, true);
document.addEventListener('mousedown',  _unlockAudio, true);

/**
 * Play a beep notification sound.
 * Works on desktop, Android, and iOS / iPadOS.
 *
 * @param {object} options
 * @param {number} options.frequency  Hz          (default 800)
 * @param {number} options.duration   ms          (default 200)
 * @param {number} options.volume     0–1         (default 0.3)
 * @param {string} options.type       oscillator  (default 'sine')
 */
export const playBeep = (options = {}) => {
  const {
    frequency = 800,
    duration  = 200,
    volume    = 0.3,
    type      = 'sine',
  } = options;

  try {
    const ctx = getAudioContext();

    if (ctx.state === 'running') {
      // Context already unlocked — fire immediately (common on desktop / after first touch)
      _fireBeep(ctx, frequency, duration, volume, type);
    } else if (ctx.state === 'suspended') {
      // Resume first, then fire — this is the Android path and the fallback for
      // iOS after the unlock listener has already run.
      ctx.resume().then(() => {
        _fireBeep(ctx, frequency, duration, volume, type);
      }).catch(err => {
        console.warn('AudioContext resume failed:', err);
      });
    }
    // 'closed' state: getAudioContext() already replaced it with a fresh one
    // above; the next call will hit the suspended branch.
  } catch (error) {
    console.warn('Could not play notification sound:', error);
  }
};

/**
 * Play a double beep for important notifications (center messages).
 */
export const playDoubleBeep = () => {
  playBeep({ frequency: 800, duration: 150 });
  setTimeout(() => {
    playBeep({ frequency: 1000, duration: 150 });
  }, 200);
};

/**
 * Play a success sound (ascending three-tone).
 */
export const playSuccessSound = () => {
  playBeep({ frequency: 600, duration: 100 });
  setTimeout(() => { playBeep({ frequency: 800, duration: 100 }); }, 120);
  setTimeout(() => { playBeep({ frequency: 1000, duration: 150 }); }, 240);
};

/**
 * Play alert sound for owner / staff activity notification (sign-in notes).
 */
export const playOwnerActivityAlert = () => {
  playBeep({ frequency: 880, duration: 150, volume: 0.4 });
  setTimeout(() => {
    playBeep({ frequency: 1100, duration: 200, volume: 0.4 });
  }, 180);
};

/**
 * Play coin / cash-register style sound for parent claim events.
 * Distinct from the center-team message double-beep and from sign-in note alerts.
 * Three quick ascending notes (C - E - G) evoke a reward/celebration feel.
 */
export const playCoinSound = () => {
  playBeep({ frequency: 523, duration: 80, volume: 0.4, type: 'triangle' });
  setTimeout(() => { playBeep({ frequency: 659, duration: 80, volume: 0.4, type: 'triangle' }); }, 90);
  setTimeout(() => { playBeep({ frequency: 784, duration: 120, volume: 0.4, type: 'triangle' }); }, 180);
};

export default { playBeep, playDoubleBeep, playSuccessSound, playOwnerActivityAlert, playCoinSound };
